import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { roleLabel, useAuth } from '../auth/AuthContext';
import { ancienParcoursEnPause, manuelVivantActif } from '../lib/manuel-vivant';
import { ApparenceToggle, FiligraneToggle } from '../theme/ApparenceToggle';
import { TuteurProvider, useTuteur } from '../tuteur/TuteurContext';

import { useConsolePerimee } from './useConsolePerimee';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly end?: boolean;
  /**
   * `tuteur` / `admin` : réservé à ce rôle. `tous` : ouvert aux deux — le seul
   * cas est la banque de savoirs, que le tuteur relit (il voit les items
   * échouer en séance) et dont l'admin répond (il tient les plafonds).
   */
  readonly role: 'tuteur' | 'admin' | 'tous';
  /**
   * Entrée du chantier « manuel vivant » : elle n'existe que si le drapeau est
   * allumé aux deux étages — build ET capacité du compte (`manuelVivantActif`).
   * Éteint, le rail est exactement celui d'avant.
   */
  readonly manuelVivant?: true;
  /**
   * Φ-MV-7 (décision F-32) — une entrée de l'ANCIEN PARCOURS, qui double ou
   * contredit le manuel : retirée du rail quand l'ancien parcours est en pause
   * (`ancienParcoursEnPause()`, `lib/manuel-vivant.ts`). Sa page reste servie
   * à son adresse. Chacune a été ouverte avant d'être marquée :
   *
   *  - « Mes classes » range des élèves dans des classes que le manuel ne lit
   *    pas (aucun écran du chantier ne filtre ni ne regroupe par classe, F-27) ;
   *  - « À valider » liste les verdicts de l'évaluation préliminaire de
   *    l'ancien parcours ; un verdict en attente garde sa carte dans « Ma
   *    journée », qui y mène ;
   *  - « Exercices » crée des exercices ciblés par savoir : le doublon de la
   *    séquence du manuel ;
   *  - « Banque de savoirs » est la banque d'items de l'ancien parcours, codes
   *    à l'écran ; le contenu du manuel se lit dans « Programme ».
   *
   * « Rapports » reste : il mène au rapport unique de chaque élève.
   */
  readonly ancienParcours?: true;
}

/**
 * L'ORDRE DU RAIL EST UNE DÉCISION DE PRODUIT (§10.1), pas un rangement.
 *
 * « Ma journée » est en premier parce que la console ouvre sur une file de
 * travail, pas sur un tableau : un tableau ne priorise pas, il présente, et
 * ouvrir sur quinze lignes rend la priorisation au tuteur qu'on prétendait
 * soulager. « Mes élèves » passe donc en deuxième entrée, pour la consultation
 * et la recherche.
 *
 * Il n'y a AUCUNE entrée « Messages » : Silex ne transporte aucun message, ni
 * tuteur↔élève ni tuteur↔parent (arbitrage du fondateur). Ce n'est pas une
 * fonction derrière un drapeau, c'est une surface qui n'existe pas.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/ma-journee', label: 'Ma journée', end: true, role: 'tuteur' },
  { to: '/eleves', label: 'Mes élèves', end: true, role: 'tuteur' },
  { to: '/classes', label: 'Mes classes', role: 'tuteur', ancienParcours: true },
  { to: '/validation', label: 'À valider', role: 'tuteur', ancienParcours: true },
  { to: '/exercices', label: 'Exercices', role: 'tuteur', ancienParcours: true },
  { to: '/rapports', label: 'Rapports', role: 'tuteur' },
  { to: '/corpus', label: 'Banque de savoirs', role: 'tous', ancienParcours: true },
  /* Φ-MV-1 — la lecture d'un chapitre. Sous la banque : c'est du contenu, que
     le tuteur enseigne et dont l'admin répond — d'où `tous`, comme elle. */
  { to: '/programme', label: 'Programme', role: 'tous', manuelVivant: true },
  { to: '/administration', label: 'Administration', role: 'admin' },
];

/**
 * LE MENU PAR TÂCHES — refonte de la console, lot L1 (15 octobre 2026).
 *
 * « Le menu nomme ce qu'on vient faire, pas où les données sont rangées »
 * (`docs/pilotage/console-refonte/AUDIT-ET-PROPOSITION.html`, § 5). Il remplace
 * le rail ci-dessus quand le manuel est allumé ET l'ancien parcours en pause —
 * c'est-à-dire en production. Le même réglage réversible (`ANCIEN_PARCOURS_EN_PAUSE`)
 * rend le rail d'avant, à l'identique.
 *
 *  - Tuteur : Aujourd'hui · Mes élèves · Contenus. Aucune inscription d'élève
 *    (décision D-5 : un tuteur ne crée pas d'élève).
 *  - Administrateur : Aujourd'hui · Contenus · Comptes · Plateforme.
 *
 * RIEN N'EST SUPPRIMÉ (décision D-6) : la banque de savoirs, l'atelier, les
 * défis, l'évaluation préliminaire (« À valider »), « Mes classes »,
 * « Exercices », « Rapports » et « Administration » restent servis à leur
 * adresse ; ils sortent seulement du menu.
 *
 * `actifSur` : les adresses (préfixes) où l'entrée est allumée — « Contenus »
 * l'est aussi sur un chapitre (`/programme/:code`), « Comptes » sur les onglets
 * d'administration qu'il regroupe.
 */
export interface EntreeTache extends NavItem {
  readonly actifSur?: readonly string[];
}

export const NAV_TACHES: readonly EntreeTache[] = [
  { to: '/ma-journee', label: 'Aujourd’hui', end: true, role: 'tuteur' },
  { to: '/aujourdhui', label: 'Aujourd’hui', end: true, role: 'admin' },
  { to: '/eleves', label: 'Mes élèves', role: 'tuteur', actifSur: ['/eleves', '/rapports'] },
  {
    to: '/contenus',
    label: 'Contenus',
    role: 'tous',
    manuelVivant: true,
    actifSur: ['/contenus', '/programme'],
  },
  {
    to: '/comptes',
    label: 'Comptes',
    role: 'admin',
    actifSur: [
      '/comptes',
      '/administration/comptes',
      '/administration/eleves',
      '/administration/affectations',
      '/administration/creation',
    ],
  },
  {
    to: '/plateforme',
    label: 'Plateforme',
    role: 'admin',
    actifSur: ['/plateforme', '/administration/consommation', '/administration/connexions'],
  },
];

/** Le menu par tâches s'applique-t-il à ce compte ? Manuel allumé ET ancien parcours en pause. */
export function menuParTaches(compte: {
  readonly manuelVivant: boolean;
  readonly ancienParcoursEnPause?: boolean;
}): boolean {
  return compte.manuelVivant && compte.ancienParcoursEnPause === true;
}

/** L'entrée est-elle celle de l'adresse courante ? */
export function entreeActive(
  item: Pick<EntreeTache, 'to' | 'end' | 'actifSur'>,
  chemin: string,
): boolean {
  const prefixes = item.actifSur ?? [item.to];
  return prefixes.some((p) =>
    item.end === true && item.actifSur === undefined
      ? chemin === p
      : chemin === p || chemin.startsWith(`${p}/`),
  );
}

/**
 * Les entrées qu'un compte voit. Hors du composant pour être éprouvée sans
 * rendu (`shell-rail.test.ts`) : c'est ici que se décide si « Programme »
 * apparaît, et qu'un rail éteint reste celui d'avant.
 */
export function entreesDuRail<T extends Pick<NavItem, 'role' | 'manuelVivant' | 'ancienParcours'>>(
  entrees: readonly T[],
  compte: {
    readonly isTuteur: boolean;
    readonly isAdmin: boolean;
    readonly manuelVivant: boolean;
    /** `ancienParcoursEnPause()` ; absent : rien n'est en pause. Un administrateur garde tout : il répond du corpus. */
    readonly ancienParcoursEnPause?: boolean;
  },
): T[] {
  return filtrerParRole(entrees, compte);
}

/** Le menu d'un compte : par tâches en production (`menuParTaches`), le rail d'avant sinon. */
export function menuDuCompte(compte: Parameters<typeof entreesDuRail>[1]): readonly EntreeTache[] {
  return menuParTaches(compte)
    ? filtrerParRole(NAV_TACHES, compte)
    : entreesDuRail(NAV_ITEMS, compte);
}

function filtrerParRole<T extends Pick<NavItem, 'role' | 'manuelVivant' | 'ancienParcours'>>(
  entrees: readonly T[],
  compte: Parameters<typeof entreesDuRail>[1],
): T[] {
  return entrees.filter((item) => {
    if (item.manuelVivant && !compte.manuelVivant) return false;
    if (item.ancienParcours && compte.ancienParcoursEnPause === true && !compte.isAdmin) {
      return false;
    }
    return item.role === 'tous'
      ? compte.isTuteur || compte.isAdmin
      : item.role === 'admin'
        ? compte.isAdmin
        : compte.isTuteur;
  });
}

function railLinkClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'rail__link active' : 'rail__link';
}

/** Initiale affichée dans l'avatar — e-mail à défaut de nom, sinon le rôle. */
function initiale(email: string | null | undefined, role: string | undefined): string {
  const source = email ?? role ?? '';
  return source.charAt(0).toUpperCase() || 'S';
}

/**
 * Coquille de la console — recette `.dash` de la maquette : rail CLAIR à 220 px
 * (marque + navigation) et zone de contenu portant le filigrane.
 *
 * Le `TuteurProvider` est monté ICI, au-dessus du rail, parce que les compteurs
 * du rail (« Ma journée 3 », « À valider 5 ») sont `FileTuteurResponse.compteurs`
 * — calculés par le serveur, pas recomptés par le client. Aucun appel n'est
 * émis pour un rôle non tuteur : `/v1/tuteur/*` répond 403 à tout autre rôle.
 */
export function Shell() {
  const { user } = useAuth();
  return (
    <TuteurProvider actif={user?.role === 'tuteur'}>
      <ShellInterne />
    </TuteurProvider>
  );
}

function ShellInterne() {
  const { user, logout } = useAuth();
  const isTuteur = user?.role === 'tuteur';
  const isAdmin = user?.role === 'admin';
  // Un admin ne voit pas les entrées tuteur : `/v1/tuteur/*` lui répond 403,
  // et une entrée de navigation qui mène à un refus est un bogue de conception.
  const { pathname } = useLocation();
  /* AB#888 : un onglet ouvert avant un déploiement se recharge à la navigation suivante. */
  useConsolePerimee();
  const items = menuDuCompte({
    isTuteur,
    isAdmin,
    manuelVivant: manuelVivantActif(),
    ancienParcoursEnPause: ancienParcoursEnPause(),
  });

  return (
    <div className="app-shell">
      <div className="app-body">
        <aside className="rail">
          <span className="rail__brand">
            Silex
            <span className="rail__dot" aria-hidden="true" />
          </span>

          <nav className="rail__nav" aria-label="Navigation principale">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={
                  item.actifSur === undefined
                    ? railLinkClass
                    : () => railLinkClass({ isActive: entreeActive(item, pathname) })
                }
              >
                <span>{item.label}</span>
                {isTuteur ? <CompteurRail to={item.to} /> : null}
              </NavLink>
            ))}
          </nav>

          <div className="rail__foot">
            <hr className="rail__sep" />
            <div className="rail__identity">
              <span className="avatar" aria-hidden="true">
                {initiale(user?.email, user?.role)}
              </span>
              <span className="rail__who">
                <span className="rail__email">
                  {user?.email ?? (user ? roleLabel(user.role) : '')}
                </span>
                {user ? <span className="rail__role">{roleLabel(user.role)}</span> : null}
              </span>
            </div>
            {/* AB#747 — « Mon compte » : ouvert à tout compte connecté. */}
            <NavLink to="/mon-compte" className={railLinkClass}>
              <span>Mon compte</span>
            </NavLink>
            <ApparenceToggle />
            <FiligraneToggle />
            <button
              type="button"
              className="btn btn--ghost btn--sm btn--block"
              onClick={() => void logout()}
            >
              Se déconnecter
            </button>
          </div>
        </aside>

        <main className="content tex-bg">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Le compteur d'une entrée du rail. Toutes les valeurs viennent du serveur :
 * `compteurs.nbUnitesFile`, `compteurs.nbEvaluationsAValider`, `nbEleves`.
 * Un signal de sécurité dans la file bascule le compteur « Ma journée » sur le
 * seul rouge autorisé de la console.
 */
function CompteurRail({ to }: { to: string }) {
  const { file, eleves } = useTuteur();

  /* En pause, « Ma journée » n'affiche pas toutes les unités de la file : le
     compte du serveur ne serait plus celui de l'écran. Pas de compteur. */
  if (to === '/ma-journee' && ancienParcoursEnPause()) return null;
  if (to === '/ma-journee' && file.status === 'ready') {
    const n = file.data.compteurs.nbUnitesFile;
    if (n === 0) return null;
    const alerte = file.data.unites.some((u) => u.type === 'signal_securite');
    return (
      <span className={alerte ? 'badge badge--rare cnt' : 'badge badge--en-cours cnt'}>{n}</span>
    );
  }
  if (to === '/validation' && file.status === 'ready') {
    const n = file.data.compteurs.nbEvaluationsAValider;
    return n > 0 ? <span className="badge badge--a-revoir cnt">{n}</span> : null;
  }
  if (to === '/eleves' && eleves.status === 'ready') {
    return <span className="rail__cnt">{eleves.data.nbEleves}</span>;
  }
  return null;
}
